import { APP_CONFIG } from "@/constants/config";

type LogLevel = "log" | "warn" | "error";

interface LogOptions {
  level?: LogLevel;
  context?: string;
  data?: unknown;
}

type LoggerInput = Omit<LogOptions, "level">;

// LocalStorage key for logger enable/disable flag
const LOGGER_ENABLED_KEY = "logger_enabled";
const PRODUCTION_APP_ENV = "production";

class Logger {
  private static isProduction(): boolean {
    const appEnv = APP_CONFIG.APP_ENV;
    return (
      String(appEnv ?? "")
        .trim()
        .toLowerCase() === PRODUCTION_APP_ENV
    );
  }

  private static isProductionLoggingOverrideEnabled(): boolean {
    return APP_CONFIG.ENABLE_LOGGER_IN_PROD;
  }

  private static formatMessage(message: string, options?: LogOptions): string {
    const context = options?.context ? `[${options.context}] ` : "";
    return `${context}${message}`;
  }

  private static getCallerSource(): string | undefined {
    const stack = new Error().stack;
    if (!stack) return undefined;

    const stackLines = stack.split("\n").map((line) => line.trim());
    for (const line of stackLines) {
      if (!line.startsWith("at ")) continue;
      if (line.includes("logger.ts") || line.includes("/helper/logger") || line.includes("Logger.")) {
        continue;
      }

      const sourceMatch = line.match(/(?:at\s+.*\()?(.+):(\d+):(\d+)\)?$/);
      if (!sourceMatch) continue;

      let sourcePath = sourceMatch[1]
        .replace(/^https?:\/\/[^/]+\//, "")
        .replace(/^webpack-internal:\/\/\/?/, "")
        .replace(/\?.*$/, "");

      const sourceIndex = sourcePath.indexOf("src/");
      if (sourceIndex >= 0) {
        sourcePath = sourcePath.slice(sourceIndex);
      } else {
        const sourceParts = sourcePath.split("/");
        sourcePath = sourceParts.slice(Math.max(sourceParts.length - 2, 0)).join("/");
      }

      return `${sourcePath}:${sourceMatch[2]}`;
    }

    return undefined;
  }

  private static getConsoleMeta(level: LogLevel): { at: string; level: LogLevel; source?: string } {
    const source = this.getCallerSource();
    return {
      at: new Date().toISOString(),
      level,
      source,
    };
  }

  private static write(level: LogLevel, message: string, input?: LoggerInput): void {
    if (this.isProduction() && !this.isProductionLoggingOverrideEnabled()) return;

    const options = input ?? {};
    const formattedMessage = this.formatMessage(message, { ...options, level });
    const hasData = Object.prototype.hasOwnProperty.call(options, "data");
    const logMeta = this.getConsoleMeta(level);
    const logArgs = hasData ? [formattedMessage, options.data, logMeta] : [formattedMessage, logMeta];

    if (level === "warn") {
      // eslint-disable-next-line no-console
      console.warn(...logArgs);
      return;
    }

    if (level === "error") {
      // eslint-disable-next-line no-console
      console.error(...logArgs);
      return;
    }

    // eslint-disable-next-line no-console
    console.log(...logArgs);
  }

  public static shouldLog(): boolean {
    if (this.isProduction() && !this.isProductionLoggingOverrideEnabled()) return false;

    try {
      // Check if logging is explicitly enabled via localStorage
      return localStorage.getItem(LOGGER_ENABLED_KEY) === "true";
    } catch {
      // LocalStorage might be unavailable in some browser contexts.
      return false;
    }
  }

  /**
   * Enable logging by setting the localStorage flag
   */
  public static enableLogging(): void {
    if (this.isProduction() && !this.isProductionLoggingOverrideEnabled()) return;

    try {
      localStorage.setItem(LOGGER_ENABLED_KEY, "true");
    } catch {
      // No-op: logger cannot persist setting in this browser context.
    }
    // eslint-disable-next-line no-console
    console.log("Logger enabled via localStorage");
  }

  /**
   * Disable logging by removing the localStorage flag
   */
  public static disableLogging(): void {
    if (this.isProduction() && !this.isProductionLoggingOverrideEnabled()) return;

    try {
      localStorage.removeItem(LOGGER_ENABLED_KEY);
    } catch {
      // No-op: logger cannot persist setting in this browser context.
    }
    // eslint-disable-next-line no-console
    console.log("Logger disabled via localStorage");
  }

  public static log(message: string, input?: LoggerInput): void {
    if (!this.shouldLog()) return;

    this.write("log", message, input);
  }

  public static warn(message: string, input?: LoggerInput): void {
    if (!this.shouldLog()) return;

    this.write("warn", message, input);
  }

  public static error(message: string, input?: LoggerInput): void {
    // Errors are still disabled in production by strict guard in write().
    this.write("error", message, input);
  }
}

// Expose logger control methods on global window object for developer convenience.
if (typeof window !== "undefined") {
  window.enableLogger = () => Logger.enableLogging();
  window.disableLogger = () => Logger.disableLogging();
}

export default Logger;
